import React, { useState, useContext } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserTie, // Directors
  faUsers, // Coordinators
  faGlobe, // Website
  faChartLine, // Analytics/Dashboard
  faEllipsisH, // More menu
  faTimes, // Close
  faChartPie, // Revenue
  faDownload, // Export
  faComments, // Social+
  faBullhorn, // PPC
  faSearch, // SEO
  faShareAlt, // Social Media
  faClipboardList, // Audit
  faFileAlt, // Content
  faSignOutAlt, // Logout
  faSync, // Sales Lifecycle
  faMoneyBillWave, // Cash Flow
  faTv, // TV Sales Stats
  faArrowDownShortWide, // Directors Upsell
  faCogs, // Engineering Dept. Performance
  faUser, // HR Dept. Performance
  faTasks, // Tasks
  faTicketAlt, // Tickets
} from "@fortawesome/free-solid-svg-icons";
import { useAuthContext } from "../../../contexts/AuthContext";
import { openURLInNewTab } from "../../Utils";
import "./MobileNavigation.css";

const MobileNavigation = () => {
  const context = useAuthContext();
  const [showFullMenu, setShowFullMenu] = useState(false);
  const location = useLocation();

  // Debug logging to understand the user object structure
  console.log("[MobileNavigation] Context:", {
    hasContext: !!context,
    hasUser: !!context?.user,
    userObject: context?.user,
    userKeys: context?.user ? Object.keys(context.user) : [],
    nestedUser: context?.user?.user,
    nestedUserKeys: context?.user?.user ? Object.keys(context.user.user) : [],
  });

  if (!context.user) return null;

  // Access the actual user data from the nested structure
  const actualUser = context.user.user || context.user;

  // Check for both 'role' and 'userRole' properties to match Sidebar component
  const userRole = actualUser.role || actualUser.userRole;

  const is_employee =
    actualUser && (userRole === "admin" || userRole === "user");
  const is_employee_admin = actualUser && userRole === "admin";

  // Debug logging for role checks
  console.log("[MobileNavigation] Role checks:", {
    actualUser,
    userRole,
    originalRole: actualUser.role,
    originalUserRole: actualUser.userRole,
    is_employee,
    is_employee_admin,
  });

  // Main navigation items (bottom 4 tabs)
  const mainNavItems = [
    {
      path: "/directorsDashboard",
      label: "Directors",
      icon: faUserTie,
      show: is_employee,
    },
    {
      path: "/coordinatorsDashboard",
      label: "Coordinators",
      icon: faUsers,
      show: is_employee,
    },
    {
      path: "/salesstats",
      label: "TV Sales Stats",
      icon: faTv,
      show: is_employee,
    },
    // Fallback items if no role-based items are shown
    {
      path: "/teams",
      label: "Teams",
      icon: faUsers,
      show: !is_employee, // Show if user is not an employee
    },
    {
      path: "/website",
      label: "Website",
      icon: faGlobe,
      show: !is_employee, // Show if user is not an employee
    },
  ].filter((item) => item.show);

  // All sidebar items, grouped as in the desktop sidebar
  const allSidebarSections = [
    {
      section: "Accountability",
      items: [
        {
          path: "/directorsDashboard",
          label: "Directors",
          icon: faUserTie,
          type: "route",
        },
        {
          path: "/coordinatorsDashboard",
          label: "Coordinators",
          icon: faUsers,
          type: "route",
        },
        {
          path: "/fulfillmentDashboard",
          label: "Fulfillment",
          icon: faTasks,
          type: "route",
        },
        {
          path: "/salesstats",
          label: "TV Sales Stats",
          icon: faTv,
          type: "route",
        },
      ],
      show: is_employee,
    },
    {
      section: "Reports",
      items: [
        {
          path: "https://gargleinc.lightning.force.com/lightning/r/Dashboard/01ZRo000002bhKzMAI/view",
          label: "Sales Insights",
          icon: faChartLine,
          type: "external",
        },
        {
          path: "https://gargleinc.lightning.force.com/lightning/r/Dashboard/01ZRo000002f6ebMAA/view?queryScope=userFolders",
          label: "Marketing Insights",
          icon: faBullhorn,
          type: "external",
        },
        {
          path: "https://gargleinc.lightning.force.com/lightning/r/Dashboard/01ZEY00000057vO2AQ/view?queryScope=userFolders",
          label: "Directors Upsell",
          icon: faArrowDownShortWide,
          type: "external",
        },
        {
          path: "https://gargleinc.lightning.force.com/lightning/r/Dashboard/01ZEY00000057so2AA/view?queryScope=userFolders",
          label: "Engineering Dept. Performance",
          icon: faCogs,
          type: "external",
        },
        {
          path: "https://gargleinc.lightning.force.com/lightning/r/Dashboard/01ZEY00000057t32AA/view?queryScope=userFolders",
          label: "Process & HR Dept. Performance",
          icon: faUser,
          type: "external",
        },
      ],
      show: is_employee,
    },
    {
      section: "Growth",
      items: [
        {
          path: "/salesFlowDashboard",
          label: "Sales Flow",
          icon: faChartLine,
          type: "route",
        },
        {
          path: "/salesLifecycleDashboard",
          label: "Sales Lifecycle",
          icon: faSync,
          type: "route",
        },
        {
          path: "/cashFlow",
          label: "Cash Flow",
          icon: faMoneyBillWave,
          type: "route",
        },
      ],
      show: is_employee_admin,
    },
    {
      section: "Data Widget",
      items: [
        {
          path: "/recordsList",
          label: "Export List",
          icon: faDownload,
          type: "route",
        },
        {
          path: "/recordsOverview",
          label: "Overview",
          icon: faChartPie,
          type: "route",
        },
      ],
      show: is_employee_admin,
    },
    {
      section: "Views",
      items: [
        { path: "/teams", label: "Teams", icon: faUsers, type: "route" },
        {
          path: "/socialplus",
          label: "Social+",
          icon: faComments,
          type: "route",
        },
        { path: "/website", label: "Website", icon: faGlobe, type: "route" },
        { path: "/ppc", label: "PPC", icon: faBullhorn, type: "route" },
        { path: "/seo", label: "SEO", icon: faSearch, type: "route" },
        {
          path: "/socialmedia",
          label: "Social Media",
          icon: faShareAlt,
          type: "route",
        },
        {
          path: "/audit",
          label: "Account Audit",
          icon: faClipboardList,
          type: "route",
        },
        { path: "/content", label: "Content", icon: faFileAlt, type: "route" },
      ],
      show: is_employee,
    },
    {
      section: "Management",
      items: [
        {
          path: "/tickets",
          label: "Tickets",
          icon: faTicketAlt,
          type: "route",
        },
        { path: "/tasks", label: "Tasks", icon: faTasks, type: "route" },
        {
          path: "/website-team",
          label: "Website Team",
          icon: faGlobe,
          type: "route",
        },
      ],
      show: is_employee,
    },
  ];

  // Debug logging for navigation items (moved after allSidebarSections is defined)
  console.log("[MobileNavigation] Navigation items:", {
    mainNavItemsCount: mainNavItems.length,
    mainNavItems,
    allSectionsCount: allSidebarSections.length,
    visibleSections: allSidebarSections.filter((section) => section.show)
      .length,
  });

  const handleNavigation = (item) => {
    if (item.type === "external") {
      openURLInNewTab(item.path);
    }
    setShowFullMenu(false);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* Mobile Bottom Navigation */}
      <div className="mobile-bottom-nav">
        {mainNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={`nav-item ${isActive(item.path) ? "active" : ""}`}
          >
            <FontAwesomeIcon icon={item.icon} />
            <span>{item.label}</span>
          </NavLink>
        ))}

        {/* More button */}
        <button
          className="nav-item more-btn"
          onClick={() => setShowFullMenu(true)}
        >
          <FontAwesomeIcon icon={faEllipsisH} />
          <span>More</span>
        </button>
      </div>

      {/* Full-screen Menu Modal */}
      {showFullMenu && (
        <div className="mobile-menu-modal">
          <div className="modal-header">
            <h2>Menu</h2>
            <button
              className="close-btn"
              onClick={() => setShowFullMenu(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="modal-content">
            <div className="user-greeting">
              <h3>
                Hello,{" "}
                {actualUser.fullName ||
                  actualUser.name ||
                  actualUser.email ||
                  "User"}
              </h3>
            </div>

            {allSidebarSections
              .filter((section) => section.show)
              .map((section) => (
                <div key={section.section} className="menu-section">
                  <h4 className="section-title">{section.section}</h4>
                  <div className="section-items">
                    {section.items.map((item) => (
                      <div key={item.path}>
                        {item.type === "route" ? (
                          <NavLink
                            to={item.path}
                            className="menu-item"
                            onClick={() => setShowFullMenu(false)}
                          >
                            <FontAwesomeIcon icon={item.icon} />
                            <span>{item.label}</span>
                          </NavLink>
                        ) : (
                          <button
                            className="menu-item"
                            onClick={() => {
                              openURLInNewTab(item.path);
                              setShowFullMenu(false);
                            }}
                          >
                            <FontAwesomeIcon icon={item.icon} />
                            <span>{item.label}</span>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

            {/* Logout */}
            <div className="menu-section">
              <div className="section-items">
                <NavLink
                  to="/logout"
                  className="menu-item logout-item"
                  onClick={() => setShowFullMenu(false)}
                >
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  <span>Logout</span>
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileNavigation;
